import React, { useState, useEffect, useContext } from 'react';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, getDocs, orderBy, updateDoc, doc, arrayUnion, arrayRemove, getDoc, setDoc, onSnapshot, increment, deleteDoc, writeBatch } from 'firebase/firestore';
import { AuthContext } from '../../AuthContext';
import { FaHeart, FaRegHeart, FaTrash, FaEdit, FaArrowLeft } from 'react-icons/fa';
import Masonry from 'react-masonry-css';
import { addDownloadTracking } from '../../utils/photoDownloadTracker';
import './BaseGallery.css';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
    arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const isVideoItem = (item) => item && item.mediaType === 'video';

// Component for individual image with download tracking
const ImageWithTracking = ({ 
    image, 
    displayTitle, 
    user, 
    onImageClick, 
    likedImages, 
    onLike, 
    isAdmin, 
    onDeleteImage, 
    deletingImage 
}) => {
    const imgRef = React.useRef(null);

    React.useEffect(() => {
        if (!isVideoItem(image) && imgRef.current && user) {
            addDownloadTracking(
                imgRef.current, 
                image.url, 
                displayTitle, 
                user
            );
        }
    }, [image.url, image.mediaType, displayTitle, user]);

    return (
        <div 
            className="masonry-item"
            onClick={(e) => onImageClick(image, e)}
        >
            {isVideoItem(image) ? (
                <video
                    src={image.url}
                    loop
                    muted
                    autoPlay
                    playsInline
                    className="masonry-media"
                    aria-label={image.filename}
                />
            ) : (
                <img 
                    ref={imgRef}
                    src={image.url} 
                    alt={image.filename}
                    loading="lazy"
                />
            )}
            <div className="image-overlay">
                <button 
                    className={`like-button ${likedImages[image.id] ? 'liked' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onLike(image.id);
                    }}
                    title={likedImages[image.id] ? "Unlike this image" : "Like this image"}
                >
                    {likedImages[image.id] ? <FaHeart /> : <FaRegHeart />} <span>{image.likes || 0}</span>
                </button>
            </div>
            
            {isAdmin && (
                <button 
                    className="delete-button"
                    onClick={(e) => onDeleteImage(image.id, image.path, e)}
                    disabled={deletingImage === image.id}
                    title="Delete this image"
                >
                    <FaTrash />
                </button>
            )}
        </div>
    );
};

// Component for lightbox image with download tracking
const LightboxImageWithTracking = ({ image, displayTitle, user }) => {
    const imgRef = React.useRef(null);

    React.useEffect(() => {
        if (!isVideoItem(image) && imgRef.current && user) {
            addDownloadTracking(
                imgRef.current, 
                image.url, 
                displayTitle, 
                user
            );
        }
    }, [image.url, image.mediaType, displayTitle, user]);

    return isVideoItem(image) ? (
        <video
            src={image.url}
            loop
            muted
            autoPlay
            playsInline
            className="lightbox-media"
            aria-label={image.filename}
        />
    ) : (
        <img 
            ref={imgRef}
            src={image.url} 
            alt={image.filename} 
        />
    );
};

// Create a sortable item component
const SortableImage = ({ image }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: image.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        touchAction: 'none',
        position: 'relative',
        zIndex: isDragging ? 1000 : 1
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`reorder-item ${isDragging ? 'dragging' : ''}`}
        >
            {isVideoItem(image) ? (
                <video
                    src={image.url}
                    loop
                    muted
                    autoPlay
                    playsInline
                    className="masonry-media"
                    aria-label={image.filename}
                    draggable={false}
                />
            ) : (
                <img 
                    src={image.url} 
                    alt={image.filename}
                    loading="lazy"
                    draggable={false}
                />
            )}
            <div className="drag-handle">
                ⋮⋮
            </div>
        </div>
    );
};

const BaseGallery = ({ collectionName, title, onBack }) => {
    const { user, isAdmin } = useContext(AuthContext);
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [userLikes, setUserLikes] = useState([]);
    const [likedImages, setLikedImages] = useState({});
    const [galleryLikes, setGalleryLikes] = useState(0);
    const [loading, setLoading] = useState(true);
    const [deletingImage, setDeletingImage] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [newGalleryTitle, setNewGalleryTitle] = useState('');
    const [customTitle, setCustomTitle] = useState(title);
    const [isReordering, setIsReordering] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);

    // Format title if not provided
    const displayTitle = customTitle || (collectionName
        ? collectionName
            .replace('gallery_', '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') + ' Gallery'
        : 'Gallery');

    const breakpointColumnsObj = {
        default: 4,
        1100: 3,
        700: 2,
        500: 1
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px movement required before drag starts
                delay: 100, // 100ms delay before drag starts
                tolerance: 5, // 5px tolerance for movement
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Function to fetch images - defined outside useEffect so it can be called from handleImageUpload
    const fetchImages = async () => {
        try {
            setLoading(true);
            const imagesCollection = collection(db, collectionName);
            const snapshot = await getDocs(imagesCollection);
            
            const imageData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                likes: doc.data().likes || 0,
                displayOrder: doc.data().displayOrder || 0
            }));
            
            // Sort by displayOrder
            imageData.sort((a, b) => a.displayOrder - b.displayOrder);
            setImages(imageData);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching images:", error);
            setLoading(false);
        }
    };

    // Fetch images and set up real-time listeners for likes
    useEffect(() => {
        if (!collectionName) return;

        fetchImages();

        // Set up real-time listener for gallery total likes
        const galleryConfigRef = collection(db, 'gallery_config');
        const unsubscribeGallery = onSnapshot(galleryConfigRef, (snapshot) => {
            const galleryConfig = snapshot.docs.find(doc => doc.data().collectionName === collectionName);
            if (galleryConfig) {
                setGalleryLikes(galleryConfig.data().totalLikes || 0);
            }
        });

        // Set up real-time listener for individual image likes and order
        const imagesRef = collection(db, collectionName);
        const unsubscribeImages = onSnapshot(imagesRef, (snapshot) => {
            const updatedImages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                likes: doc.data().likes || 0,
                displayOrder: doc.data().displayOrder || 0 // Ensure displayOrder exists
            }));
            
            // Sort by displayOrder
            updatedImages.sort((a, b) => a.displayOrder - b.displayOrder);
            setImages(updatedImages);
        });

        // Fetch user's liked images
        const fetchUserLikes = async () => {
            if (!user?.uid) return;
            
            try {
                const userLikesRef = doc(db, 'user_likes', user.uid);
                const userLikesDoc = await getDoc(userLikesRef);
                
                if (userLikesDoc.exists()) {
                    const userData = userLikesDoc.data();
                    setLikedImages(userData.likedImages || {});
                } else {
                    // Create the document if it doesn't exist
                    await setDoc(userLikesRef, { likedImages: {} });
                    setLikedImages({});
                }
            } catch (error) {
                console.error("Error fetching user likes:", error);
            }
        };

        fetchUserLikes();

        return () => {
            unsubscribeGallery();
            unsubscribeImages();
        };
    }, [collectionName, user]);

    const getMediaType = (file) =>
        file.type.startsWith('video/') ? 'video' : 'image';

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(true);
        setError(null);

        try {
            // Get the current highest displayOrder before the loop
            const currentMaxOrder = images.reduce((max, img) => 
                Math.max(max, img.displayOrder || 0), -1);

            for (const [index, file] of files.entries()) {
                const path = `${collectionName}/${user.uid}/${Date.now()}_${file.name}`;
                const storageRef = ref(storage, path);
                
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);

                await addDoc(collection(db, collectionName), {
                    url: downloadURL,
                    filename: file.name,
                    timestamp: new Date().toISOString(),
                    userId: user.uid,
                    path: path,
                    likes: 0,
                    likedBy: [],
                    displayOrder: currentMaxOrder + 1 + index,
                    mediaType: getMediaType(file)
                });
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            setError("Failed to upload. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const handleLike = async (imageId) => {
        if (!user?.uid) return;
        
        try {
            // Check if the image is already liked by this user
            const isLiked = likedImages[imageId];
            
            // Update image likes in Firestore
            const imageRef = doc(db, collectionName, imageId);
            await updateDoc(imageRef, {
                likes: increment(isLiked ? -1 : 1),
                likedBy: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid)
            });
            
            // Update gallery total likes
            const galleryConfigRef = collection(db, 'gallery_config');
            const gallerySnapshot = await getDocs(galleryConfigRef);
            const galleryDoc = gallerySnapshot.docs.find(doc => doc.data().collectionName === collectionName);
            
            if (galleryDoc) {
                await updateDoc(doc(db, 'gallery_config', galleryDoc.id), {
                    totalLikes: increment(isLiked ? -1 : 1)
                });
            }
            
            // Update user's liked images
            const userLikesRef = doc(db, 'user_likes', user.uid);
            
            if (isLiked) {
                // Remove the like
                await updateDoc(userLikesRef, {
                    [`likedImages.${imageId}`]: false
                });
            } else {
                // Add the like
                await updateDoc(userLikesRef, {
                    [`likedImages.${imageId}`]: true
                });
            }
            
            // Update local state after Firestore updates
            setLikedImages(prev => ({
                ...prev,
                [imageId]: !isLiked
            }));
            
        } catch (error) {
            console.error("Error updating likes:", error);
        }
    };

    const handleImageClick = (image, e) => {
        // Prevent opening lightbox when clicking the like button
        if (e.target.closest('.like-button')) {
            return;
        }
        
        const index = images.findIndex(img => img.id === image.id);
        setCurrentImageIndex(index);
        setSelectedImage(image);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setSelectedImage(null);
        document.body.style.overflow = 'auto';
    };

    const showNextImage = (e) => {
        e.stopPropagation();
        const nextIndex = (currentImageIndex + 1) % images.length;
        setCurrentImageIndex(nextIndex);
        setSelectedImage(images[nextIndex]);
    };

    const showPrevImage = (e) => {
        e.stopPropagation();
        const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
        setCurrentImageIndex(prevIndex);
        setSelectedImage(images[prevIndex]);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!selectedImage) return;
            
            switch (event.keyCode) {
                case 27: // ESC
                    closeLightbox();
                    break;
                case 37: // Left arrow
                    showPrevImage(event);
                    break;
                case 39: // Right arrow
                    showNextImage(event);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, currentImageIndex, images]);

    const handleDeleteImage = async (imageId, imagePath, e) => {
        e.stopPropagation();
        
        if (!isAdmin || !imageId) return;
        
        try {
            setDeletingImage(imageId);
            
            // Delete from Firestore
            await deleteDoc(doc(db, collectionName, imageId));
            
            // Delete from Storage if path exists
            if (imagePath) {
                const storageRef = ref(storage, imagePath);
                await deleteObject(storageRef);
            }
            
            // Update UI optimistically
            setImages(prevImages => prevImages.filter(img => img.id !== imageId));
            
            // If the deleted image was selected in lightbox, close it
            if (selectedImage && selectedImage.id === imageId) {
                closeLightbox();
            }
        } catch (error) {
            console.error("Error deleting image:", error);
            setError(`Failed to delete image: ${error.message}`);
        } finally {
            setDeletingImage(null);
        }
    };

    // Function to handle gallery rename
    const handleRenameGallery = async (e) => {
        e.preventDefault();
        
        if (!isAdmin || !newGalleryTitle.trim()) return;
        
        try {
            // Get the gallery config document
            const galleryConfigRef = collection(db, 'gallery_config');
            const snapshot = await getDocs(galleryConfigRef);
            const galleryConfig = snapshot.docs.find(doc => doc.data().collectionName === collectionName);
            
            if (galleryConfig) {
                // Update existing config
                await updateDoc(doc(db, 'gallery_config', galleryConfig.id), {
                    displayTitle: newGalleryTitle.trim()
                });
            } else {
                // Create new config if it doesn't exist
                await addDoc(collection(db, 'gallery_config'), {
                    collectionName: collectionName,
                    displayTitle: newGalleryTitle.trim(),
                    totalLikes: galleryLikes || 0
                });
            }
            
            // Update local state
            setCustomTitle(newGalleryTitle.trim());
            setShowRenameModal(false);
            setNewGalleryTitle('');
            
        } catch (error) {
            console.error("Error renaming gallery:", error);
            setError(`Failed to rename gallery: ${error.message}`);
        }
    };

    // Fetch gallery config including custom title
    useEffect(() => {
        if (!collectionName) return;
        
        const fetchGalleryConfig = async () => {
            try {
                const galleryConfigRef = collection(db, 'gallery_config');
                const snapshot = await getDocs(galleryConfigRef);
                const galleryConfig = snapshot.docs.find(doc => doc.data().collectionName === collectionName);
                
                if (galleryConfig && galleryConfig.data().displayTitle) {
                    setCustomTitle(galleryConfig.data().displayTitle);
                }
            } catch (error) {
                console.error("Error fetching gallery config:", error);
            }
        };
        
        fetchGalleryConfig();
        
        // Set up real-time listener for gallery config changes
        const galleryConfigRef = collection(db, 'gallery_config');
        const unsubscribeConfig = onSnapshot(galleryConfigRef, (snapshot) => {
            const galleryConfig = snapshot.docs.find(doc => doc.data().collectionName === collectionName);
            if (galleryConfig) {
                if (galleryConfig.data().displayTitle) {
                    setCustomTitle(galleryConfig.data().displayTitle);
                }
                setGalleryLikes(galleryConfig.data().totalLikes || 0);
            }
        });
        
        return () => {
            unsubscribeConfig();
        };
    }, [collectionName]);

    const handleOrderChange = async (imageId, newOrder) => {
        try {
            const orderNum = parseInt(newOrder) || 0; // Convert to number, default to 0 if NaN
            if (orderNum < 0) return; // Prevent negative numbers

            const imageRef = doc(db, collectionName, imageId);
            await updateDoc(imageRef, {
                displayOrder: orderNum
            });
        } catch (error) {
            console.error("Error updating order:", error);
        }
    };

    // Replace the DnD reordering UI with numerical inputs
    const ReorderMode = () => (
        <div className="reorder-grid">
            {images.map((image) => (
                <div key={image.id} className="reorder-item">
                    {isVideoItem(image) ? (
                        <video
                            src={image.url}
                            loop
                            muted
                            autoPlay
                            playsInline
                            className="masonry-media"
                            aria-label={image.filename}
                        />
                    ) : (
                        <img 
                            src={image.url} 
                            alt={image.filename}
                            loading="lazy"
                        />
                    )}
                    <div className="order-input-container">
                        <input
                            type="number"
                            min="0"
                            value={image.displayOrder || 0} // Ensure a valid value
                            onChange={(e) => handleOrderChange(image.id, e.target.value)}
                            className="order-input"
                        />
                    </div>
                </div>
            ))}
        </div>
    );

    if (loading) {
        return <div className="gallery-loading">Loading gallery...</div>;
    }

    return (
        <div className="gallery-container">
            <div className="gallery-header">
                {onBack && (
                    <button className="back-button" onClick={onBack}>
                        <FaArrowLeft /> Back
                    </button>
                )}
                <h2 className="gallery-title">{displayTitle}</h2>
                <div className="copyright-notice">© 2026 Moon Park LLC. All rights reserved.</div>
                {isAdmin && (
                    <button 
                        className="rename-button"
                        onClick={() => {
                            setNewGalleryTitle(customTitle || displayTitle);
                            setShowRenameModal(true);
                        }}
                        title="Rename Gallery"
                    >
                        <span style={{ fontSize: '24px', fontWeight: 'bold' }}>✎</span>
                    </button>
                )}
            </div>
            
            {isAdmin && (
                <div className="admin-controls">
                    <div className="upload-section">
                        <input
                            type="file"
                            accept="image/*,video/mp4,video/webm,video/quicktime"
                            onChange={handleImageUpload}
                            disabled={uploading}
                            id={`image-upload-${collectionName}`}
                            className="hidden-input"
                        />
                        <label htmlFor={`image-upload-${collectionName}`} className="upload-button">
                            {uploading ? 'Uploading...' : 'Upload Image, GIF or Short Video'}
                        </label>
                        {error && <p className="error-message">{error}</p>}
                    </div>
                    <button 
                        className="reorder-toggle"
                        onClick={() => setIsReordering(!isReordering)}
                    >
                        {isReordering ? 'Save Order' : 'Reorder Images'}
                    </button>
                </div>
            )}

            <div className="gallery-stats">
                <span className="total-likes">
                    <FaHeart className="heart-icon" /> {galleryLikes} total likes
                </span>
            </div>

            {isAdmin && isReordering ? (
                <ReorderMode />
            ) : (
                <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="my-masonry-grid"
                    columnClassName="my-masonry-grid_column"
                >
                    {images.map((image) => (
                        <ImageWithTracking
                            key={image.id}
                            image={image}
                            displayTitle={displayTitle}
                            user={user}
                            onImageClick={handleImageClick}
                            likedImages={likedImages}
                            onLike={handleLike}
                            isAdmin={isAdmin}
                            onDeleteImage={handleDeleteImage}
                            deletingImage={deletingImage}
                        />
                    ))}
                </Masonry>
            )}

            {selectedImage && (
                <div className="lightbox" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>×</button>
                    
                    <button 
                        className="lightbox-nav-button prev"
                        onClick={showPrevImage}
                        aria-label="Previous image"
                    >
                        ‹
                    </button>
                    
                    <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                        <LightboxImageWithTracking
                            image={selectedImage}
                            displayTitle={displayTitle}
                            user={user}
                        />
                        <div className="lightbox-caption">
                            <div className="lightbox-actions">
                                <button 
                                    className={`like-button lightbox-like ${likedImages[selectedImage.id] ? 'liked' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLike(selectedImage.id);
                                    }}
                                    title={likedImages[selectedImage.id] ? "Unlike this image" : "Like this image"}
                                >
                                    {likedImages[selectedImage.id] ? <FaHeart /> : <FaRegHeart />} <span>{selectedImage.likes || 0}</span>
                                </button>
                                
                                {isAdmin && (
                                    <button 
                                        className="delete-button lightbox-delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteImage(selectedImage.id, selectedImage.path, e);
                                        }}
                                        disabled={deletingImage === selectedImage.id}
                                        title="Delete this image"
                                    >
                                        <FaTrash />
                                    </button>
                                )}
                                
                                <span className="image-counter">
                                    {currentImageIndex + 1} / {images.length}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button 
                        className="lightbox-nav-button next"
                        onClick={showNextImage}
                        aria-label="Next image"
                    >
                        ›
                    </button>
                </div>
            )}

            {/* Rename Gallery Modal */}
            {showRenameModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Rename Gallery</h3>
                        <form onSubmit={handleRenameGallery}>
                            <input
                                type="text"
                                value={newGalleryTitle}
                                onChange={(e) => setNewGalleryTitle(e.target.value)}
                                placeholder="Enter new gallery name"
                                required
                            />
                            <div className="modal-buttons">
                                <button type="button" onClick={() => setShowRenameModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit">
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BaseGallery;